import {
  Box,
  CircularProgress,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { makeStyles } from "@mui/styles";
import ModalBase from "../../components/ModalBase";
import CustomButton from "../../components/CustomButton";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import {
  ClientErrorLogDto,
  LogManager,
} from "../../services/api/LogManager";

interface ErrorLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatDate = (value: string): string => {
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleString();
};

const ErrorLogsModal: FC<ErrorLogsModalProps> = ({ isOpen, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [logs, setLogs] = useState<ClientErrorLogDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await LogManager.getManager().getClientErrorLogs();
      setLogs(result);
    } catch (e: any) {
      showFlashMessageWithTimeout(
        dispatch,
        e?.toString?.() ?? "Failed to load error logs",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (isOpen) {
      fetchLogs();
    }
  }, [isOpen, fetchLogs]);

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} center>
      <Box className={classes.container}>
        {/* Header */}
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {t("admin.errorLogs.title") || "Error logs"}
          </Typography>
          <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <RefreshIcon
              style={{
                color: theme.palette.main.primary.o,
                cursor: "pointer",
              }}
              onClick={fetchLogs}
            />
            <CloseIcon
              style={{ color: theme.palette.natural.t2, cursor: "pointer" }}
              onClick={onClose}
            />
          </Box>
        </Box>

        <Divider style={{ marginBottom: 16 }} />

        {/* Body */}
        {isLoading ? (
          <Box className={classes.centered}>
            <CircularProgress
              style={{ color: theme.palette.main.primary.o }}
            />
          </Box>
        ) : logs.length === 0 ? (
          <Box className={classes.centered}>
            <Typography className={classes.empty}>
              {t("admin.errorLogs.empty") || "No errors logged."}
            </Typography>
          </Box>
        ) : (
          <Box className={classes.list}>
            {logs.map(log => (
              <Box key={log.id} className={classes.logItem}>
                <Box className={classes.logHeader}>
                  <Typography className={classes.logMessage}>
                    {log.message}
                  </Typography>
                  <Typography className={classes.logDate}>
                    {formatDate(log.createdAt)}
                  </Typography>
                </Box>
                <Box className={classes.logMeta}>
                  {log.source && (
                    <span className={classes.sourceTag}>{log.source}</span>
                  )}
                  {log.category && (
                    <span className={classes.tag}>{log.category}</span>
                  )}
                  {log.userEmail && (
                    <span className={classes.metaText}>{log.userEmail}</span>
                  )}
                  {log.url && (
                    <span className={classes.metaText}>{log.url}</span>
                  )}
                </Box>
                {log.context && (
                  <pre className={classes.context}>{log.context}</pre>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Footer */}
        <Box className={classes.footer}>
          <CustomButton
            title={t("close") || "Close"}
            type='secondary'
            onClick={onClose}
            style={{ width: 120 }}
          />
        </Box>
      </Box>
    </ModalBase>
  );
};

const useStyles = makeStyles((theme: any) => ({
  container: {
    backgroundColor: theme.palette.natural.t6,
    borderRadius: 12,
    padding: "32px",
    minWidth: 680,
    maxWidth: 820,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: theme.palette.main.secondary.u1,
  },
  centered: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 160,
  },
  empty: {
    fontSize: 15,
    color: theme.palette.natural.t2,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    maxHeight: 480,
    overflowY: "auto",
    marginBottom: 24,
  },
  logItem: {
    padding: 16,
    backgroundColor: theme.palette.natural.t,
    borderRadius: 8,
    border: `1px solid ${theme.palette.natural.t4}`,
  },
  logHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
  },
  logMessage: {
    fontSize: 15,
    fontWeight: 600,
    color: theme.palette.natural.t1,
    wordBreak: "break-word",
  },
  logDate: {
    fontSize: 12,
    color: theme.palette.natural.t2,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  logMeta: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
  },
  tag: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.main.primary.o,
    backgroundColor: theme.palette.main.primary.d3,
    borderRadius: 6,
    padding: "2px 8px",
  },
  sourceTag: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.natural.t,
    backgroundColor: theme.palette.natural.t2,
    borderRadius: 6,
    padding: "2px 8px",
    textTransform: "uppercase",
  },
  metaText: {
    fontSize: 12,
    color: theme.palette.natural.t2,
    wordBreak: "break-all",
  },
  context: {
    marginTop: 8,
    marginBottom: 0,
    padding: 8,
    fontSize: 12,
    color: theme.palette.natural.t2,
    backgroundColor: theme.palette.natural.t6,
    borderRadius: 6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: 160,
    overflowY: "auto",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: 16,
    borderTop: `1px solid ${theme.palette.natural.t4}`,
  },
}));

export default ErrorLogsModal;
